/**
 * Pre-flight payload-size estimation for the study-start request.
 *
 * The study-start request (`POST /optimize`) embeds the full uploaded OHLCV dataset
 * inline. Large intraday datasets can exceed the transport/server body limit and fail
 * at submit time. These helpers let the UI estimate the payload size up front and warn
 * the user honestly BEFORE they submit — never to silently truncate or alter their data.
 *
 * The estimate is intentionally cheap (candle count × bytes-per-candle) so it works on
 * very large arrays without serializing them; it is used only for warnings/guards.
 */
import {
  ESTIMATED_BYTES_PER_CANDLE,
  MAX_REQUEST_PAYLOAD_BYTES,
} from "@/config";

/** Estimate the serialized request payload size, in bytes, from a candle count. */
export function estimateOptimizationPayloadBytes(candleCount: number): number {
  if (!Number.isFinite(candleCount) || candleCount <= 0) {
    return 0;
  }
  return Math.round(candleCount * ESTIMATED_BYTES_PER_CANDLE);
}

/** Format a byte count as a human-readable string (e.g. "4.5 MB"). */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "0 B";
  }
  const units = ["B", "KB", "MB", "GB"];
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / Math.pow(1024, exponent);
  // Whole bytes have no decimals; KB+ get one decimal place.
  const decimals = exponent === 0 ? 0 : 1;
  return `${value.toFixed(decimals)} ${units[exponent]}`;
}

export interface PayloadSizeAssessment {
  candleCount: number;
  estimatedBytes: number;
  estimatedFormatted: string;
  limitBytes: number;
  limitFormatted: string;
  exceedsLimit: boolean;
}

/**
 * Assess whether a dataset is likely too large to send as a study-start request.
 *
 * @param candleCount Number of OHLCV candles in the dataset.
 * @param limitBytes  Maximum payload size to allow (defaults to MAX_REQUEST_PAYLOAD_BYTES,
 *                    which mirrors the backend guard).
 */
export function assessOptimizationPayload(
  candleCount: number,
  limitBytes: number = MAX_REQUEST_PAYLOAD_BYTES,
): PayloadSizeAssessment {
  const estimatedBytes = estimateOptimizationPayloadBytes(candleCount);
  return {
    candleCount: candleCount > 0 ? candleCount : 0,
    estimatedBytes,
    estimatedFormatted: formatBytes(estimatedBytes),
    limitBytes,
    limitFormatted: formatBytes(limitBytes),
    exceedsLimit: estimatedBytes > limitBytes,
  };
}
